import { inject, Injectable } from '@angular/core'
import { Params, Router } from '@angular/router'
import { GbDashboardDialogService } from '@galabid/gb-dashboard/shared/utils'
import { GbErrorMessageService, ObjectUtils } from '@galabid/shared/utils/helpers'
import { GbNotificationService } from '@galabid/shared/utils/notification'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { AuctionsConstants } from 'libs/gb-dashboard/auctions/feature/src/lib/auctions.constants'
import { EventTicketsConstants } from 'libs/gb-dashboard/event-tickets/feature/src/lib/event-tickets-constants'
import { GbEventTicketPurchaseDialogComponent } from 'libs/gb-dashboard/event-tickets/feature/src/lib/gb-event-ticket-purchase-dialog/gb-event-ticket-purchase-dialog.component'
import { GbImageUploadService } from 'libs/shared/images/utils/src/lib/gb-image-upload-service/gb-image-upload.service'
import { GbLoaderService } from 'libs/shared/utils/loader/src/lib/services/gb-loader.service'
import { catchError, concatMap, exhaustMap, filter, finalize, map, of, switchMap, tap, withLatestFrom } from 'rxjs'
import { EventTicketPackagesApiService } from './event-ticket-packages-api.service'
import * as EventTicketPackagesActions from './event-ticket-packages.actions'
import { EventTicketPackagesFacade } from './event-ticket-packages.facade'
import {
  EVENT_TICKET_PACKAGES_LOADER,
  EventTicketPackageEntity,
  EventTicketPackageImagesEntity,
} from './event-ticket-packages.models'
import { GbNavigationService } from 'libs/shared/utils/services/src/lib/gb-navigation-service/gb-navigation.service'
import { PendingChangesService } from 'libs/gb-dashboard/shared/utils/src/lib/gb-pending-changes/gb-pending-changes.service'
import { GbEventTicketPackageSelectService } from 'libs/gb-dashboard/event-tickets/utils/src/lib/gb-event-ticket-package-select/gb-event-ticket-package-select.service'
import * as ImagesActions from 'libs/gb-dashboard/shared/data-access-images/src/lib/+state/images.actions'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'
import { TranslateService } from '@ngx-translate/core'

@Injectable()
export class EventTicketPackagesEffects {
  private readonly actions$ = inject(Actions)
  private readonly eventTicketPackagesApiService = inject(EventTicketPackagesApiService)
  private readonly eventTicketPackagesFacade = inject(EventTicketPackagesFacade)
  private readonly selectedAuctionFacade = inject(SelectedAuctionFacade)
  private readonly gbDashboardDialogService = inject(GbDashboardDialogService)
  private readonly gbImageUploadService = inject(GbImageUploadService)
  private readonly gbNotificationService = inject(GbNotificationService)
  private readonly gbErrorMessageService = inject(GbErrorMessageService)
  private readonly router = inject(Router)
  private readonly gbNavigationService = inject(GbNavigationService)
  private readonly gbLoaderService = inject(GbLoaderService)
  private readonly pendingChangesService = inject(PendingChangesService)
  private readonly eventTicketPackageSelectService = inject(GbEventTicketPackageSelectService)
  private readonly translateService = inject(TranslateService)

  loadEventTicketPackages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPackagesActions.loadEventTicketPackages),
      withLatestFrom(this.selectedAuctionFacade.urlName$, this.eventTicketPackagesFacade.allEventTicketPackages$),
      switchMap(([action, urlName, allEventTicketPackages]) => {
        let offset = allEventTicketPackages.length || 0
        if (action.shouldReset) {
          offset = 0
        }
        return this.eventTicketPackagesApiService
          .getEventTicketPackages(urlName, offset, action.searchTerm, action.order, action.limit)
          .pipe(
            map((response) => {
              const payload = {
                eventTicketPackages: response.results,
                count: response.count,
                filters: ObjectUtils.cleanObject({ search: action.searchTerm, order: action.order }),
              }

              return action.shouldReset
                ? EventTicketPackagesActions.setEventTicketPackages(payload)
                : EventTicketPackagesActions.loadEventTicketPackagesSuccess(payload)
            }),
            catchError((error) => of(EventTicketPackagesActions.loadEventTicketPackagesFailure({ error }))),
          )
      }),
    ),
  )

  loadEventTicketPackagesFromDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPackagesActions.loadEventTicketPackagesFromDetail),
      withLatestFrom(
        this.selectedAuctionFacade.urlName$,
        this.eventTicketPackagesFacade.currentFilters$,
        this.eventTicketPackagesFacade.allEventTicketPackages$,
      ),
      switchMap(([, urlName, currentFilters, allItems]) =>
        this.eventTicketPackagesApiService
          .getEventTicketPackages(urlName, allItems.length, currentFilters.search, currentFilters.order)
          .pipe(
            map((response) =>
              EventTicketPackagesActions.loadEventTicketPackagesSuccess({
                eventTicketPackages: response.results,
                count: response.count,
                filters: ObjectUtils.cleanObject(currentFilters),
              }),
            ),
            catchError((error) => of(EventTicketPackagesActions.loadEventTicketPackagesFailure({ error }))),
          ),
      ),
    ),
  )

  createEventTicketPackage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPackagesActions.createEventTicketPackage),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      concatMap(([action, urlName]) =>
        this.eventTicketPackagesApiService
          .createEventTicketPackage({
            ...action.eventTicketPackage,
            auction: urlName,
          })
          .pipe(
            map((response) =>
              EventTicketPackagesActions.createEventTicketPackageSuccess({ eventTicketPackage: response }),
            ),
            catchError((error) => of(EventTicketPackagesActions.createEventTicketPackageFailure({ error }))),
          ),
      ),
    ),
  )

  createEventTicketPackageSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventTicketPackagesActions.createEventTicketPackageSuccess),
        withLatestFrom(this.selectedAuctionFacade.urlName$),
        tap(([, urlName]) => {
          this.pendingChangesService.resetPendingChanges()
          this.gbNotificationService.success('EVENT_TICKET_PACKAGES.EDIT.CREATE_SUCCESS')
          this.router.navigate([
            `/${AuctionsConstants.AUCTION}/${urlName}/${EventTicketsConstants.EVENT_TICKETS}/${EventTicketsConstants.EVENT_TICKETS_LIST}`,
          ])
        }),
      ),
    { dispatch: false },
  )

  bulkUpdateEventTicketPackages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPackagesActions.bulkUpdateEventTicketPackages),
      tap(() => this.gbLoaderService.showLoaderDialog(EVENT_TICKET_PACKAGES_LOADER.bulkUpdateEventTicketPackages.id)),
      withLatestFrom(
        this.selectedAuctionFacade.urlName$,
        this.eventTicketPackageSelectService.selectedEventTicketPackageUuids$,
      ),
      switchMap(([action, urlName, selectedEventTicketPackageUuids]) =>
        this.eventTicketPackagesApiService
          .bulkUpdateEventTicketPackages(urlName, {
            ticket_packages: selectedEventTicketPackageUuids,
            ticket_package: action.eventTicketPackage,
          })
          .pipe(
            map(() => EventTicketPackagesActions.bulkUpdateEventTicketPackagesSuccess()),
            catchError((error) => of(EventTicketPackagesActions.bulkUpdateEventTicketPackagesFailure({ error }))),
            finalize(() =>
              this.gbLoaderService.hideLoaderDialog(EVENT_TICKET_PACKAGES_LOADER.bulkUpdateEventTicketPackages.id),
            ),
          ),
      ),
    ),
  )

  bulkUpdateEventTicketPackagesSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventTicketPackagesActions.bulkUpdateEventTicketPackagesSuccess),
        withLatestFrom(this.selectedAuctionFacade.urlName$),
        tap(([, urlName]) => {
          this.pendingChangesService.overridePendingChanges(false)
          this.gbNotificationService.success('EVENT_TICKET_PACKAGES.BULK_UPDATE.SUCCESS')
          this.router.navigate([
            `/${AuctionsConstants.AUCTION}/${urlName}/${EventTicketsConstants.EVENT_TICKETS}/${EventTicketsConstants.EVENT_TICKETS_LIST}`,
          ])
        }),
      ),
    { dispatch: false },
  )

  clearBulkUpdateSelection$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          EventTicketPackagesActions.bulkUpdateEventTicketPackagesSuccess,
          EventTicketPackagesActions.bulkUpdateEventTicketPackagesFailure,
          EventTicketPackagesActions.bulkDeleteEventTicketPackagesSuccess,
          EventTicketPackagesActions.bulkDeleteEventTicketPackagesFailure,
        ),
        tap(() => {
          this.eventTicketPackageSelectService.deselectAllEventTicketPackages()
          this.pendingChangesService.overridePendingChanges(false)
        }),
      ),
    { dispatch: false },
  )

  checkEventTicketPackagesForDelete$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventTicketPackagesActions.checkEventTicketPackagesForDelete),
      withLatestFrom(this.eventTicketPackageSelectService.selectedEventTicketPackagesHaveActivity$),
      map(([, selectedEventTicketPackagesHaveActivity]) => {
        if (selectedEventTicketPackagesHaveActivity) {
          return EventTicketPackagesActions.showHasActivityWarning()
        } else {
          return EventTicketPackagesActions.bulkDeleteEventTicketPackages()
        }
      }),
    )
  })

  bulkDeleteEventTicketPackages$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventTicketPackagesActions.bulkDeleteEventTicketPackages),
      withLatestFrom(this.eventTicketPackageSelectService.selectedEventTicketPackagesCount$),
      exhaustMap(([action, selectedCount]) => {
        const confirmBody =
          selectedCount > 1
            ? 'EVENT_TICKET_PACKAGES.BULK_DELETE.CONFIRM_BODY'
            : 'EVENT_TICKET_PACKAGES.BULK_DELETE.CONFIRM_BODY_SINGLE'
        return this.gbDashboardDialogService.filterByConfirmDialog(
          {
            title: 'EVENT_TICKET_PACKAGES.BULK_DELETE.CONFIRM_TITLE',
            body: this.translateService.instant(confirmBody, { count: selectedCount }),
            confirmButtonText: 'EVENT_TICKET_PACKAGES.LIST.CONFIRM_DELETE_CONFIRM',
            cancelButtonText: 'EVENT_TICKET_PACKAGES.LIST.CONFIRM_DELETE_CANCEL',
          },
          action,
        )
      }),
      tap(() => this.gbLoaderService.showLoaderDialog(EVENT_TICKET_PACKAGES_LOADER.bulkDeleteEventTicketPackages.id)),
      withLatestFrom(
        this.selectedAuctionFacade.urlName$,
        this.eventTicketPackageSelectService.selectedEventTicketPackageUuids$,
      ),
      switchMap(([, urlName, selectedEventTicketPackageUuids]) => {
        return this.eventTicketPackagesApiService
          .bulkDeleteEventTicketPackages(urlName, selectedEventTicketPackageUuids)
          .pipe(
            map(() =>
              EventTicketPackagesActions.bulkDeleteEventTicketPackagesSuccess({
                ticketPackages: selectedEventTicketPackageUuids,
              }),
            ),
            catchError((error) => of(EventTicketPackagesActions.bulkDeleteEventTicketPackagesFailure({ error }))),
            finalize(() =>
              this.gbLoaderService.hideLoaderDialog(EVENT_TICKET_PACKAGES_LOADER.bulkDeleteEventTicketPackages.id),
            ),
          )
      }),
    )
  })

  deleteEventTicketPackage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPackagesActions.deleteEventTicketPackage),
      exhaustMap((action) =>
        this.gbDashboardDialogService.filterByConfirmDialog(
          {
            title: 'EVENT_TICKET_PACKAGES.LIST.CONFIRM_DELETE_TITLE',
            body: 'EVENT_TICKET_PACKAGES.LIST.CONFIRM_DELETE_BODY',
            confirmButtonText: 'EVENT_TICKET_PACKAGES.LIST.CONFIRM_DELETE_CONFIRM',
            cancelButtonText: 'EVENT_TICKET_PACKAGES.LIST.CONFIRM_DELETE_CANCEL',
          },
          action,
        ),
      ),
      tap(() =>
        this.gbLoaderService.showLoaderDialog(
          EVENT_TICKET_PACKAGES_LOADER.deleteEventTicketPackage.id,
          EVENT_TICKET_PACKAGES_LOADER.deleteEventTicketPackage.message,
        ),
      ),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) =>
        this.eventTicketPackagesApiService.deleteEventTicketPackage(urlName, action.uuid).pipe(
          map(() => EventTicketPackagesActions.deleteEventTicketPackageSuccess({ uuid: action.uuid })),
          catchError((error) => of(EventTicketPackagesActions.deleteEventTicketPackageFailure({ error }))),
          finalize(() =>
            this.gbLoaderService.hideLoaderDialog(EVENT_TICKET_PACKAGES_LOADER.deleteEventTicketPackage.id),
          ),
        ),
      ),
    ),
  )

  deleteEventTicketPackageSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventTicketPackagesActions.deleteEventTicketPackageSuccess),
        tap(() => this.gbNotificationService.success('EVENT_TICKET_PACKAGES.LIST.DELETE_SUCCESS')),
      ),
    { dispatch: false },
  )

  goToEditEventTicketPackage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventTicketPackagesActions.goToEditEventTicketPackage),
        withLatestFrom(this.selectedAuctionFacade.urlName$),
        tap(([action, urlName]) =>
          this.router.navigate([
            `/${AuctionsConstants.AUCTION}/${urlName}/${EventTicketsConstants.EVENT_TICKETS}/${EventTicketsConstants.EDIT_TICKET}/${action.uuid}`,
          ]),
        ),
      ),
    { dispatch: false },
  )

  syncEventTicketPackage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPackagesActions.syncEventTicketPackage),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) => {
        return this.eventTicketPackagesApiService.syncEventTicketPackage(urlName, action.uuid).pipe(
          map((response) => EventTicketPackagesActions.syncEventTicketPackageSuccess({ eventTicketPackage: response })),
          catchError((error) => of(EventTicketPackagesActions.syncEventTicketPackageFailure({ error }))),
        )
      }),
    ),
  )

  syncEventTicketPackageForRealtime$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPackagesActions.syncEventTicketPackageForRealtime),
      filter(
        (action) =>
          // only sync if admin is on edit page for updated ticket package or on the ticket package list
          this.router.url.includes(`${EventTicketsConstants.EDIT_TICKET}/${action.uuid}`) ||
          this.router.url.includes(EventTicketsConstants.EVENT_TICKETS_LIST),
      ),
      withLatestFrom(this.eventTicketPackagesFacade.allEventTicketPackages$),
      map(([action, allEventTicketPackages]) => {
        const isEditPage = this.router.url.includes(EventTicketsConstants.EDIT_TICKET)

        if (isEditPage) {
          return EventTicketPackagesActions.showContentUpdatedToast({
            onConfirm: () => EventTicketPackagesActions.syncEventTicketPackage({ uuid: action.uuid }),
          })
        }

        const isTicketPackageInList = allEventTicketPackages.some((ticketPackage) => ticketPackage.uuid === action.uuid)

        return isTicketPackageInList
          ? EventTicketPackagesActions.syncEventTicketPackage({ uuid: action.uuid })
          : EventTicketPackagesActions.loadEventTicketPackages({ shouldReset: true })
      }),
    ),
  )

  showContentUpdatedToast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPackagesActions.showContentUpdatedToast),
      switchMap((action) =>
        this.gbNotificationService.yesNo('CONTENT_UPDATED').pipe(
          filter((response) => response),
          map(() => action.onConfirm()),
        ),
      ),
    ),
  )

  updateSelectedEventTicketPackage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPackagesActions.updateSelectedEventTicketPackage),
      withLatestFrom(this.eventTicketPackagesFacade.selectedEventTicketPackage$),
      switchMap(([action, selectedEventTicketPackage]) => {
        return this.eventTicketPackagesApiService
          .updateEventTicketPackage({
            ...(selectedEventTicketPackage as EventTicketPackageEntity),
            ...action.eventTicketPackageUpdate,
          })
          .pipe(
            map((response) =>
              EventTicketPackagesActions.updateSelectedEventTicketPackageSuccess({
                update: { id: response.uuid, changes: response },
              }),
            ),
            catchError((error) => of(EventTicketPackagesActions.updateSelectedEventTicketPackageFailure({ error }))),
            finalize(() => {
              if (action.onComplete) action.onComplete()
            }),
          )
      }),
    ),
  )

  updateSelectedEventTicketPackageSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventTicketPackagesActions.updateSelectedEventTicketPackageSuccess),
        tap(() => this.gbNotificationService.success('EVENT_TICKET_PACKAGES.EDIT.UPDATE_SUCCESS')),
      ),
    { dispatch: false },
  )

  showHasActivityWarning$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventTicketPackagesActions.showHasActivityWarning),
        exhaustMap(() =>
          this.gbNotificationService
            .message('EVENT_TICKET_PACKAGES.WARNING_NOTIFICATION', 'EVENT_TICKET_PACKAGES.GO_TO_PURCHASES')
            .onAction(),
        ),
        withLatestFrom(this.selectedAuctionFacade.urlName$),
        tap(([, urlName]) => {
          this.router.navigate([
            `/${AuctionsConstants.AUCTION}/${urlName}/${EventTicketsConstants.EVENT_TICKETS}/${EventTicketsConstants.PURCHASES_LIST}`,
          ])
        }),
      ),
    { dispatch: false },
  )

  openEventTicketPurchaseDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPackagesActions.openEventTicketPurchaseDialog),
      switchMap(() =>
        this.gbDashboardDialogService
          .getDialogObs(GbEventTicketPurchaseDialogComponent, {
            disableClose: true,
            width: '600px',
            mobileFullScreen: true,
          })
          .pipe(map(() => EventTicketPackagesActions.clearSelectedEventTicketPackage())),
      ),
    ),
  )

  closeEventTicketPurchaseDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventTicketPackagesActions.closeEventTicketPurchaseDialog),
        map(() => this.gbDashboardDialogService.closeDialog(GbEventTicketPurchaseDialogComponent)),
      ),
    { dispatch: false },
  )

  showErrorToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          EventTicketPackagesActions.loadEventTicketPackagesFailure,
          EventTicketPackagesActions.createEventTicketPackageFailure,
          EventTicketPackagesActions.bulkUpdateEventTicketPackagesFailure,
          EventTicketPackagesActions.deleteEventTicketPackageFailure,
          EventTicketPackagesActions.bulkDeleteEventTicketPackagesFailure,
          EventTicketPackagesActions.syncEventTicketPackageFailure,
          EventTicketPackagesActions.updateSelectedEventTicketPackageFailure,
        ),
        map((action) => {
          const error = this.gbErrorMessageService.getErrorMessage(action.error.error)
          this.gbNotificationService.error(error || 'GENERIC.ERROR')
        }),
      ),
    { dispatch: false },
  )

  updatedSelectedTicketPackageImages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPackagesActions.updateSelectedTicketPackageImages),
      withLatestFrom(
        this.eventTicketPackagesFacade.selectedEventTicketPackage$,
        this.gbImageUploadService.updatedImages$,
      ),
      switchMap(([, selectedEventTicketPackage, updatedImages]) => {
        return this.eventTicketPackagesApiService
          .updateEventTicketPackage({
            ...(selectedEventTicketPackage as EventTicketPackageEntity),
            ...{ images: updatedImages as EventTicketPackageImagesEntity[] },
          })
          .pipe(
            map((response) =>
              EventTicketPackagesActions.updateSelectedEventTicketPackageSuccess({
                update: {
                  id: response.uuid,
                  changes: response,
                },
              }),
            ),
            catchError((error) => of(EventTicketPackagesActions.updateSelectedEventTicketPackageFailure(error))),
          )
      }),
    ),
  )

  navigateToEventTicketPackage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventTicketPackagesActions.navigateToEventTicketPackage),
        withLatestFrom(this.selectedAuctionFacade.urlName$),
        tap(([action, urlName]) => {
          let params: Params = {}
          const route = `/${AuctionsConstants.AUCTION}/${urlName}/${EventTicketsConstants.EVENT_TICKETS}/${EventTicketsConstants.EDIT_TICKET}/${action.uuid}`

          if (action.backPath) {
            params = { backPath: action.backPath }
          }

          this.gbNavigationService.navigate(route, { newTab: action.newTab, params })
        }),
      ),
    { dispatch: false },
  )

  reorderEventTicketPackages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPackagesActions.reorderEventTicketPackages),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) => {
        return this.eventTicketPackagesApiService.reorderEventTicketPackages(urlName, 1, action.uuids).pipe(
          map((response) =>
            EventTicketPackagesActions.reorderEventTicketPackagesSuccess({ uuids: response.ticket_packages }),
          ),
          catchError((error) => of(EventTicketPackagesActions.reorderEventTicketPackagesFailure({ error }))),
        )
      }),
    ),
  )

  reloadEventTicketPackagesOnReorderSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPackagesActions.reorderEventTicketPackagesSuccess),
      map(() =>
        EventTicketPackagesActions.loadEventTicketPackages({
          shouldReset: true,
        }),
      ),
    ),
  )

  openCreateEventTicketPackageImageUploadDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPackagesActions.openCreateEventTicketPackageImageUploadDialog),
      map((action) =>
        EventTicketPackagesActions.openEventTicketPackageImageUploadDialog({
          images: action.images,
          onSuccess: action.onSuccess,
        }),
      ),
    ),
  )

  openUpdateEventTicketPackageImageUploadDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPackagesActions.openUpdateEventTicketPackageImageUploadDialog),
      withLatestFrom(this.eventTicketPackagesFacade.selectedEventTicketPackage$),
      map(([action, selectedEventTicketPackage]) =>
        EventTicketPackagesActions.openEventTicketPackageImageUploadDialog({
          images: selectedEventTicketPackage?.images,
          onSuccess: (images) =>
            EventTicketPackagesActions.updateSelectedEventTicketPackage({
              eventTicketPackageUpdate: {
                images: images?.map((image) => ({
                  ...image,
                  ticket_package: selectedEventTicketPackage?.uuid || '',
                  uuid: '',
                  order: image.order || 0,
                })),
              },
              ...(action.fromList
                ? { onComplete: () => EventTicketPackagesActions.clearSelectedEventTicketPackage() }
                : {}),
            }),
        }),
      ),
    ),
  )

  openEventTicketPackageImageUploadDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPackagesActions.openEventTicketPackageImageUploadDialog),
      map((action) =>
        ImagesActions.openImageUploadDialog({
          images: action.images,
          config: {
            buttonText: 'EVENT_TICKET_PACKAGES.EDIT.CHOOSE_IMAGE',
            maxHeight: '1000',
            maxWidth: '1000',
            maxSize: '8',
            maxImages: '20',
            isMulti: true,
            collapsePreview: true,
          },
          onSuccess: (images) => action.onSuccess(images),
        }),
      ),
    ),
  )
}
